/**
 * SellerOrdersTab — order list with status/return filters + per-order
 * action card. Extracted from SellerPage.jsx in iter-41.
 *
 * Exports:
 *   - SellerOrdersTab (default): the filter row + order list orchestrator.
 *   - ShippingCountriesCard: per-store ships-to picker (used in Stores tab).
 *   - FULFILLMENT_FLOW, NEXT_STATUS, STATUS_LABEL, STATUS_COLOR, RETURN_COLOR:
 *     status constants reused elsewhere.
 */
import React, { useEffect, useState } from "react";
import {
  Truck, CheckCircle2, Clock, Undo2, ThumbsUp, ThumbsDown,
  MessageCircle, Send, User as UserIcon,
} from "lucide-react";
import { api } from "../../lib/api";
import { CountryMultiSelect } from "../ProfileFields";
import { useSellerCurrency } from "../../lib/sellerCurrency";

export const FULFILLMENT_FLOW = ["confirmed", "processing", "shipped", "out_for_delivery", "delivered"];
export const NEXT_STATUS = {
  confirmed: "processing",
  processing: "shipped",
  shipped: "out_for_delivery",
  out_for_delivery: "delivered",
};
export const STATUS_LABEL = {
  confirmed: "Confirmed", processing: "Mark processing", shipped: "Mark shipped",
  out_for_delivery: "Out for delivery", delivered: "Mark delivered",
  cancelled: "Cancelled", refunded: "Refunded", exchanged: "Exchanged",
};
export const STATUS_COLOR = (s) => ({
  confirmed: "#D4FF00", processing: "#D4FF00", shipped: "#00F0FF",
  out_for_delivery: "#00F0FF", delivered: "#D4FF00",
  cancelled: "#FF007F", refunded: "#FF007F", exchanged: "#A1A1AA",
}[s] || "#FAFAFA");
export const RETURN_COLOR = (rs) => ({
  requested: "#FFA500", approved: "#00F0FF", rejected: "#FF007F",
  picked_up: "#00F0FF", received: "#00F0FF", refunded: "#FF007F",
}[rs] || "#A1A1AA");


export function ShippingCountriesCard({ stores, onSaved }) {
  const [storeId, setStoreId] = useState("");
  const [ships, setShips] = useState(["ALL"]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => {
    if (!storeId && stores?.length) setStoreId(stores[0].store_id);
  }, [stores, storeId]);
  useEffect(() => {
    if (!storeId) return;
    api.get(`/seller/stores/${storeId}/shipping-countries`)
      .then(({ data }) => setShips(data.ships_to || ["ALL"]))
      .catch(() => setShips(["ALL"]));
  }, [storeId]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/seller/stores/${storeId}/shipping-countries`, { ships_to: ships });
      setMsg("Shipping countries saved.");
      onSaved && onSaved();
    } catch (e) { setMsg("Save failed."); }
    finally { setBusy(false); setTimeout(() => setMsg(""), 2000); }
  };
  const applyAll = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm("Apply these shipping countries to every store you own?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/seller/stores/shipping-countries/apply-all`,
                                       { ships_to: ships });
      setMsg(`Applied to ${data.applied_count} stores.`);
    } catch { setMsg("Apply-all failed."); }
    finally { setBusy(false); setTimeout(() => setMsg(""), 2000); }
  };

  if (!stores?.length) return null;

  return (
    <div className="card p-5 mb-6" data-testid="shipping-card">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-3">
        <div>
          <div className="font-display font-bold uppercase text-sm flex items-center gap-2">
            <span className="text-xl">🌍</span> Shipping countries
          </div>
          <p className="text-[11px] text-[#A1A1AA] mt-1">
            Buyers outside these countries won't see your products or streams. Default is worldwide.
          </p>
        </div>
        <div className="flex gap-2">
          <select className="input-dark" value={storeId}
            data-testid="shipping-store-picker"
            onChange={(e) => setStoreId(e.target.value)}>
            {stores.map(s => (
              <option key={s.store_id} value={s.store_id}>{s.name}</option>
            ))}
          </select>
          <button className="btn-primary" onClick={save} disabled={busy} data-testid="shipping-save-btn">
            Save
          </button>
          {stores.length > 1 && (
            <button className="btn-secondary" onClick={applyAll} disabled={busy}
              data-testid="shipping-apply-all-btn">
              Apply to all
            </button>
          )}
        </div>
      </div>
      <CountryMultiSelect value={ships} onChange={setShips} testid="shipping"/>
      {msg && <div className="mt-2 text-xs text-[#D4FF00]" data-testid="shipping-toast">{msg}</div>}
    </div>
  );
}


function SellerOrderCard({ order, onUpdateStatus, onConfirmPayment, onDecideReturn, onMarkPickup, onComplete }) {
  const o = order;
  const { fmt } = useSellerCurrency();
  const next = NEXT_STATUS[o.status];
  const isClosed = ["cancelled", "refunded"].includes(o.status);
  const buyer = o.buyer || {};
  const missing = o.buyer_missing_fields || [];
  const [nudging, setNudging] = useState(false);
  const [nudgedAt, setNudgedAt] = useState(o.buyer_nudged_at || null);
  const phoneStr = buyer.phone
    ? `${buyer.phone_cc ? `+${buyer.phone_cc} ` : ""}${buyer.phone}`
    : null;
  const addrObj = buyer.address_obj || {};
  const orderAddr = o.address || {};
  const addrParts = [
    orderAddr.line1 || addrObj.line1,
    orderAddr.line2 || addrObj.line2,
    orderAddr.city || addrObj.city,
    orderAddr.state || addrObj.state,
    orderAddr.zip || addrObj.zip || addrObj.postal_code,
    orderAddr.country || addrObj.country,
  ].filter(Boolean);
  const fullAddress = addrParts.join(", ");

  const nudge = async () => {
    setNudging(true);
    try {
      const { data } = await api.post(`/seller/orders/${o.order_id}/nudge-buyer`);
      if (data.skipped) {
        alert("Buyer's contact info is already complete — nothing to nudge for.");
      } else {
        setNudgedAt(data.nudged_at);
        alert(`Nudged buyer about: ${(data.missing || []).join(", ")}`);
      }
    } catch (e) {
      alert(e?.response?.data?.detail || "Nudge failed");
    } finally { setNudging(false); }
  };

  const Pill = ({ label, value, missing: m, icon }) => (
    <div className="flex items-start gap-1.5 min-w-0">
      {icon}
      <div className="min-w-0">
        <div className="text-[9px] uppercase tracking-widest font-bold text-[#A1A1AA]">{label}</div>
        {value ? (
          <div className="text-xs font-bold truncate">{value}</div>
        ) : (
          <div className="text-[10px] uppercase font-bold tracking-widest text-[#FF007F] bg-[#FF007F]/10 px-1.5 py-0.5 rounded inline-block">
            Missing
          </div>
        )}
        {value && m && <div className="text-[9px] text-[#FF007F]">{m}</div>}
      </div>
    </div>
  );

  return (
    <div className="card p-4" data-testid={`seller-order-${o.order_id}`}>
      <div className="flex flex-wrap items-start gap-3 mb-3">
        <img src={buyer.picture} className="w-10 h-10 rounded-full" alt="" />
        <div className="flex-1 min-w-[180px]">
          <div className="font-bold text-sm">#{o.order_id.slice(-8).toUpperCase()}</div>
          <div className="text-xs text-[#A1A1AA] truncate">
            {new Date(o.created_at).toLocaleString()}
          </div>
        </div>
        <div className="text-right">
          <div className="font-display font-bold text-[#D4FF00]">{fmt(o.seller_subtotal ?? o.total)}</div>
          <div className="text-[10px] text-[#A1A1AA] uppercase tracking-widest">Your share</div>
        </div>
        <div className="text-right">
          <span className="text-[10px] uppercase font-bold tracking-widest px-2 py-1 rounded"
            style={{ color: STATUS_COLOR(o.status), background: `${STATUS_COLOR(o.status)}1F` }}>
            {o.status.replace("_", " ")}
          </span>
          {o.return_status && o.return_status !== "none" && (
            <div className="text-[10px] uppercase font-bold tracking-widest mt-1 px-2 py-0.5 rounded inline-block"
              style={{ color: RETURN_COLOR(o.return_status), background: `${RETURN_COLOR(o.return_status)}1F` }}>
              return · {o.return_status.replace("_", " ")}
            </div>
          )}
        </div>
      </div>

      <div className="mb-3 border border-[#3D2845] rounded-lg p-3 bg-[#1A0F1F]"
        data-testid={`seller-order-buyer-${o.order_id}`}>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA]">Buyer contact</span>
          {missing.length > 0 && (
            <span className="text-[9px] uppercase tracking-widest font-bold text-[#FF007F] bg-[#FF007F]/15 px-1.5 py-0.5 rounded"
              data-testid={`seller-order-missing-${o.order_id}`}>
              {missing.length} missing
            </span>
          )}
          {missing.length > 0 && (
            <button onClick={nudge}
              disabled={nudging}
              data-testid={`seller-order-nudge-${o.order_id}`}
              className="ml-auto text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded bg-[#D4FF00] text-black hover:opacity-90 disabled:opacity-50 flex items-center gap-1">
              <Send size={10}/>
              {nudging ? "Sending…" : nudgedAt ? "Nudge again" : "Nudge buyer"}
            </button>
          )}
          {nudgedAt && (
            <span className="text-[9px] text-[#A1A1AA]" data-testid={`seller-order-nudged-${o.order_id}`}>
              Nudged {new Date(nudgedAt).toLocaleString()}
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Pill label="Name" value={buyer.name}
            icon={<UserIcon size={12} className="text-[#A1A1AA] mt-0.5"/>}/>
          {buyer.email ? (
            <a href={`mailto:${buyer.email}`} className="flex items-start gap-1.5 min-w-0 hover:text-[#00F0FF]"
              data-testid={`seller-order-email-${o.order_id}`}>
              <MessageCircle size={12} className="text-[#A1A1AA] mt-0.5"/>
              <div className="min-w-0">
                <div className="text-[9px] uppercase tracking-widest font-bold text-[#A1A1AA]">Email</div>
                <div className="text-xs font-bold truncate">{buyer.email}</div>
              </div>
            </a>
          ) : (
            <Pill label="Email" value={null}
              icon={<MessageCircle size={12} className="text-[#A1A1AA] mt-0.5"/>}/>
          )}
          {phoneStr ? (
            <a href={`tel:${(buyer.phone_cc ? "+" + buyer.phone_cc : "") + buyer.phone}`}
              className="flex items-start gap-1.5 min-w-0 hover:text-[#00F0FF]"
              data-testid={`seller-order-phone-${o.order_id}`}>
              <Send size={12} className="text-[#A1A1AA] mt-0.5"/>
              <div className="min-w-0">
                <div className="text-[9px] uppercase tracking-widest font-bold text-[#A1A1AA]">Phone</div>
                <div className="text-xs font-bold truncate">{phoneStr}</div>
              </div>
            </a>
          ) : (
            <Pill label="Phone" value={null}
              icon={<Send size={12} className="text-[#A1A1AA] mt-0.5"/>}/>
          )}
          <Pill label="Ship to" value={fullAddress || null}
            icon={<Truck size={12} className="text-[#A1A1AA] mt-0.5"/>}/>
        </div>
      </div>

      <div className="space-y-1 mb-3 max-h-48 overflow-y-auto pr-1">
        {(o.seller_items || o.items || []).map((it, i) => (
          <div key={`${it.product_id}-${it.size || ""}-${i}`} className="flex items-center gap-2 text-xs">
            <img src={it.image} className="w-8 h-8 rounded object-cover" alt="" />
            <div className="flex-1 min-w-0 truncate">{it.product_name}</div>
            <div className="text-[#A1A1AA]">×{it.quantity}</div>
            <div className="font-bold text-[#D4FF00] w-16 text-right">{fmt(it.price * it.quantity)}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {o.payment_status === "pending" && (
          <button
            onClick={() => onConfirmPayment(o.order_id)}
            data-testid={`seller-order-confirm-payment-${o.order_id}`}
            className="btn-primary text-xs px-3 py-2" style={{background:"#FFA500", color:"#000"}}>
            <CheckCircle2 size={12} className="inline mr-1"/> Confirm payment ({(o.payment_method || "").toUpperCase()})
          </button>
        )}
        {!isClosed && next && (
          <button onClick={() => onUpdateStatus(o.order_id, next)}
            data-testid={`seller-order-next-${o.order_id}`}
            className="btn-primary text-xs px-3 py-2">
            <Truck size={12} className="inline mr-1" /> {STATUS_LABEL[next]}
          </button>
        )}
        {!isClosed && o.status !== "delivered" && o.status !== "confirmed" && (
          <button onClick={() => onUpdateStatus(o.order_id, "delivered")}
            className="btn-secondary text-xs px-3 py-2"
            data-testid={`seller-order-deliver-${o.order_id}`}>
            <CheckCircle2 size={12} className="inline mr-1" /> Mark delivered
          </button>
        )}

        {o.return_status === "requested" && (
          <>
            <button onClick={() => onDecideReturn(o.order_id, "approve")}
              data-testid={`seller-return-approve-${o.order_id}`}
              className="btn-secondary text-xs px-3 py-2">
              <ThumbsUp size={12} className="inline mr-1" /> Approve return
            </button>
            <button onClick={() => onDecideReturn(o.order_id, "reject")}
              data-testid={`seller-return-reject-${o.order_id}`}
              className="text-xs px-3 py-2 rounded-full bg-[#FF007F]/15 text-[#FF007F] border border-[#FF007F]/30 hover:bg-[#FF007F]/25 transition">
              <ThumbsDown size={12} className="inline mr-1" /> Reject
            </button>
          </>
        )}
        {o.return_status === "approved" && (
          <button onClick={() => onMarkPickup(o.order_id)}
            data-testid={`seller-return-pickup-${o.order_id}`}
            className="btn-secondary text-xs px-3 py-2">
            <Truck size={12} className="inline mr-1" /> Picked up
          </button>
        )}
        {(o.return_status === "approved" || o.return_status === "picked_up") && (
          <button onClick={() => onComplete(o.order_id)}
            data-testid={`seller-return-complete-${o.order_id}`}
            className="btn-primary text-xs px-3 py-2">
            <Undo2 size={12} className="inline mr-1" /> Received · refund
          </button>
        )}
      </div>

      {(o.tracking || []).length > 0 && (
        <details className="mt-3 text-xs">
          <summary className="cursor-pointer text-[#A1A1AA] hover:text-[#FAFAFA]">
            <Clock size={10} className="inline mr-1" /> Tracking ({o.tracking.length})
          </summary>
          <div className="mt-2 space-y-1 pl-3 border-l border-[#3D2845]">
            {[...o.tracking].reverse().map((t) => (
              <div key={`${t.at}-${t.status}`} className="flex gap-2">
                <div className="text-[#D4FF00] font-bold uppercase">{t.label}</div>
                <div className="text-[#A1A1AA] text-[10px]">{new Date(t.at).toLocaleString()}</div>
                {t.note && <div className="text-[#A1A1AA] truncate">· {t.note}</div>}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}


export default function SellerOrdersTab({
  orders, statusFilter, setStatusFilter, returnFilter, setReturnFilter,
  onUpdateStatus, onConfirmPayment, onDecideReturn, onMarkPickup, onComplete,
}) {
  return (
    <div className="space-y-3">
      <div className="card p-3 flex flex-wrap items-center gap-2">
        <select className="input-dark sm:w-44" value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)} data-testid="seller-orders-status">
          <option value="">All statuses</option>
          {FULFILLMENT_FLOW.map(s => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          <option value="cancelled">cancelled</option>
          <option value="refunded">refunded</option>
        </select>
        <select className="input-dark sm:w-44" value={returnFilter}
          onChange={e => setReturnFilter(e.target.value)} data-testid="seller-orders-return">
          <option value="">All returns</option>
          <option value="requested">Pending decision</option>
          <option value="approved">Approved</option>
          <option value="picked_up">Picked up</option>
          <option value="rejected">Rejected</option>
          <option value="refunded">Refunded</option>
        </select>
        <div className="ml-auto text-xs text-[#A1A1AA]">{orders.length} orders</div>
      </div>

      {orders.length === 0 && (
        <div className="card p-8 text-center text-[#A1A1AA]">No orders match these filters.</div>
      )}

      {orders.map(o => (
        <SellerOrderCard key={o.order_id} order={o}
          onUpdateStatus={onUpdateStatus}
          onConfirmPayment={onConfirmPayment}
          onDecideReturn={onDecideReturn}
          onMarkPickup={onMarkPickup}
          onComplete={onComplete} />
      ))}
    </div>
  );
}
